import { useQuery } from 'react-query';

import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import type { IProperty, ITourIntegration } from 'src/services/api/properties/types';
import type { ITouringHours } from 'src/services/api/touring-hours/types';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';

export interface IGetPropertyOptions {
    /**
     * Parent information is not fetched by default.
     * If set to true, also fetch information about all ancestors in a chain as high as it goes.
     */
    include_ancestors?: boolean;
    /**
     * Property facts not fetched by default.
     * If set to true, fetch all facts about the current property known to the system.
     */
    include_facts?: boolean;
    /**
     * Tour-scheduling integrations (e.g. Elise.ai) not fetched by default.
     * If set to true, fetch all tour integrations configured for this property.
     */
    include_integrations?: boolean;
    /**
     * Effective touring hours not fetched by default.
     * If set to true, also fetch this property's effective (inheritance-aware) touring hours.
     */
    include_touring_hours?: boolean;
}

/** Resolved tour-scheduling integrations for a property, including inheritance metadata. */
export interface IEffectiveTourIntegrations {
    property_id: number;
    /**
     * The property that actually owns `integrations` (the root building; equal
     * to `property_id` when that property has no parent).
     */
    source_property_id: number;
    integrations: ITourIntegration[];
}

type IGetPropertyResponse = {
    /**
     * If `includeFacts` is true then `facts` is guaranteed to be populated.
     * If `includeAncestors` is true then `parent` is guaranteed to be populated.
     */
    property: IProperty;
    /** Present only when `include_integrations` is requested. */
    integrations?: IEffectiveTourIntegrations;
    /** Present only when `include_touring_hours` is requested. */
    touring_hours?: ITouringHours;
};

/** `IProperty` plus `integrations`/`touring_hours`, when requested. */
export type IPropertyWithIntegrations = IProperty & {
    integrations?: IEffectiveTourIntegrations;
    touring_hours?: ITouringHours;
};

export const normalizeOptions = (rawOptions?: IGetPropertyOptions): IGetPropertyOptions => {
    const {
        include_ancestors = false,
        include_facts = false,
        include_integrations = false,
        include_touring_hours = false,
    } = rawOptions ?? {};
    return {
        include_ancestors,
        include_facts,
        include_integrations,
        include_touring_hours,
    };
};

/**
 * Load information about a specific property.
 * Ideally we have that information cached.
 * For default options, should reuse the cache in `useProperties`.
 *
 * @param refetchOnWindowFocus Defaults to the app-wide default (true). Pass
 * `false` for callers that render an editable form from this data (e.g. a
 * create/edit property modal) — a background refetch on window focus would
 * otherwise silently overwrite the user's in-progress, unsaved edits.
 */
export const useProperty = (
    id?: number | null,
    options?: IGetPropertyOptions,
    refetchOnWindowFocus?: boolean,
) => {
    // this is for consistent cache keys with use-properties
    const normalizedOptions = normalizeOptions(options);

    return useQuery(
        [queryKeys.properties, id, normalizedOptions],
        async () => {
            const searchParams = new URLSearchParams();
            if (normalizedOptions.include_ancestors) {
                searchParams.append('include_ancestors', '1');
            }
            if (normalizedOptions.include_facts) {
                searchParams.append('include_facts', '1');
            }
            if (normalizedOptions.include_integrations) {
                searchParams.append('include_integrations', '1');
            }
            if (normalizedOptions.include_touring_hours) {
                searchParams.append('include_touring_hours', '1');
            }

            const path = `${GET_PROPERTIES_PATH}/${id}`;
            const res = await apiClient(path, {
                searchParams
            }).json<IGetPropertyResponse>();
            return {
                ...res.property,
                integrations: res.integrations,
                touring_hours: res.touring_hours,
            } as IPropertyWithIntegrations;
        },
        {
            enabled: !!id,
            ...(refetchOnWindowFocus !== undefined ? { refetchOnWindowFocus } : {}),
        }
    );
};
